<template>
  <Container
    v-if="showPolicyGroupTemplates"
    :class="$style.templateContainer"
  >
    <HeadingSmall>
      <IconButton
        :class="$style.backButton"
        data-test-id="PolicyGroupTemplateManage__BackButton"
        hasNoBackground
        icon="arrowLeft"
        text="Policy Group Home"
        type="informational"
        @click="handleBackButtonClick"
      />
    </HeadingSmall>
    <ItemListCollectionWrapper
      :class="$style.wrapperProps"
      :collectionModule="storeModule"
      defaultSortBy="name"
      :refreshKey="0"
      rowsPerPageLocalStorageKey="03-10-2023-policy-group-templates-per-page"
    >
      <template v-slot="slotProps">
        <ItemListPagination
          :class="$style.groupPolicyTemplateList"
          :columns="columns"
          :currentPageNum="slotProps.currentPageNum"
          :isLoading="slotProps.loading"
          :itemComponent="$options.PolicyGroupTemplateManageItemRow"
          :rowsPerPage="slotProps.rowsPerPage"
          :selectable="false"
          :showEmptyState="slotProps.showEmptyState"
          :sortBy="slotProps.sortBy"
          :sortDirection="slotProps.sortDirection"
          :tableData="getCollection"
          :totalCount="slotProps.totalCount"
          @pageChange="slotProps.handlePageChange"
          @sortColumn="slotProps.handleSortColumn"
        >
          <template v-slot:toolbar>
            <Grid
              :class="$style.toolbar"
              hasGap
            >
              <GridItem
                :class="$style.searchBar"
                :gridSpan="6"
              >
                <InputSearch
                  :focus="slotProps.shouldFocusSearch"
                  placeholder="Search"
                  :value="slotProps.getSearchTerm"
                  @input="(term) => handleEnterSearchTerm(term, slotProps.handleEnterSearchTerm)"
                />
              </GridItem>
              <GridItem
                :class="$style.countItemsLabel"
                :gridSpan="6"
              >
                {{ totalTemplates }}
              </GridItem>
            </Grid>
          </template>

          <template v-slot:emptyState>
            <NoSearchResults
              :ctaText="emptyState.ctaText"
              :instructions="emptyState.instructions"
              @click="handleCtaClick"
            />
          </template>
        </ItemListPagination>
      </template>
    </ItemListCollectionWrapper>
  </Container>
</template>

<script>
import {
  Constants,
  Container,
  Grid,
  GridItem,
  HeadingSmall,
  IconButton,
  InputSearch,
  ItemListPagination,
  NoSearchResults,
  Types,
} from '@jumpcloud/ui-components';
import { ItemTypeConsts, ItemTypeRoutes, StoreModuleMap } from 'client/util/Constants';
import { debounce, noop } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import ItemListCollectionWrapper from 'client/components/ItemList/ItemListCollectionWrapper.vue';
import MspState from 'client/components/models/MspState';
import pluralize from 'pluralize';

import PolicyGroupTemplateManageItemRow from 'client/groups/Configuration/PolicyGroupTemplateManageItemRow.vue';

const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

const { Column } = Types;

const { alignment } = Constants;

export default {
  name: 'PolicyGroupTemplateManage',

  components: {
    Container,
    Grid,
    GridItem,
    HeadingSmall,
    IconButton,
    InputSearch,
    ItemListPagination,
    ItemListCollectionWrapper,
    NoSearchResults,
  },

  data() {
    return {
      customTotalCount: null,
      itemType: ItemTypeConsts.customPolicyGroupTemplates,
      renderTable: false,
      emptyState: {
        ctaText: 'create a new policy group template',
        instructions: 'If you didn\'t find the template you were looking for,',
      },

      columns: [
        Column({
          dataFieldName: 'name',
          label: 'Name',
          sortable: true,
          width: '4fr',
        }),
        Column({
          dataFieldName: 'description',
          label: 'Description',
          sortable: false,
          width: '7fr',
        }),
        Column({
          dataFieldName: 'actionButtons',
          label: '',
          width: '3fr',
          alignment: alignment.right,
        }),
      ],
    };
  },

  PolicyGroupTemplateManageItemRow,

  computed: {
    ...mapGetters('CustomPolicyGroupTemplateCollection', ['getCollection']),

    ...mapState('CustomPolicyGroupTemplateCollection', {
      stateCount: (state) => state.entities.items.totalCount,
    }),

    ...mapState(settingsModule, [
      'user',
    ]),

    ...mapGetters(settingsModule, [
      'isAdminWithBilling',
      'isAdmin',
      'isManager',
    ]),

    showPolicyGroupTemplates() {
      return this.$hasFeatureFlag(this.$featureFlags.policyGroupTemplateManage)
        && MspState.isAnMsp
        && (this.isAdminWithBilling || this.isAdmin || this.isManager);
    },

    storeModule() {
      return StoreModuleMap[this.itemType];
    },

    totalTemplates() {
      const countState = this.stateCount;
      const label = `${countState} ${pluralize('template', countState)}`;
      return label;
    },
  },

  async created() {
    this.setProviderId(this.user.provider);
    await this.fetchCollection();
  },

  methods: {
    ...mapActions('CustomPolicyGroupTemplateCollection', [
      'fetchCollection',
      'setProviderId',
    ]),

    handleBackButtonClick() {
      const configurationGroupRoute = ItemTypeRoutes[ItemTypeConsts.configurationGroup];
      this.$router.push(configurationGroupRoute).catch(noop);
    },

    handleCtaClick() {
      this.$router.push(`${ItemTypeRoutes[ItemTypeConsts.configurationGroup]}/new`);
    },

    handleEnterSearchTerm: debounce(async (term, func) => {
      await func(term);
    }, 250),
  },
};
</script>

<style module scoped>

.backButton.backButton {
  margin-bottom: 0;
  margin-left: 1em;
}

.countItemsLabel {
  color: var(--jc-color-disabled-text);
  padding-top: 1rem;
  text-align: left;
}

.searchBar {
  margin-bottom: 1rem;
}

.templateContainer.templateContainer {
  min-height: 50rem;
}

.wrapperProps.wrapperProps {
  min-height: 60rem;
}

</style>
